import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useAppState, useDispatch } from '../state/AppContext';
import { ADD_SEED_ARTIST } from '../state/actions';
import { findArtistTrack } from '../api/musicClient';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { buildArtistPageUrl } from '../utils/exportUtils';
import '../styles/favorites.css';
import '../styles/friends.css';

function DiscoveredSection({ filterQuery = '' }) {
  const { user, discoveredArtists, favorites, discoveredConnections } = useAuth();
  const { toggleDiscoveredArtist, showAuthModal, loadDiscoveredConnections } = useAuthActions();
  const { seedArtists } = useAppState();
  const dispatch = useDispatch();
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [loadingTrack, setLoadingTrack] = useState(null);
  const trackCache = useRef({});
  const audio = useAudioPreview();

  // Fetch connection data when the discovered list or favorites change.
  // The endpoint is cheap once the similar-artist cache is warm; cold cache
  // triggers a one-time Last.fm fetch and then persists.
  useEffect(() => {
    if (user && discoveredArtists.length > 0) {
      loadDiscoveredConnections();
    }
  }, [user, discoveredArtists.length, favorites.length, loadDiscoveredConnections]);

  const filteredDiscovered = useMemo(() => {
    if (!filterQuery.trim()) return discoveredArtists;
    const q = filterQuery.toLowerCase();
    return discoveredArtists.filter((a) => a.artistName.toLowerCase().includes(q));
  }, [discoveredArtists, filterQuery]);

  const formatDiscoveredDate = (value) => {
    if (!value) return '';
    const iso = typeof value === 'string' && value.includes(' ') && !value.includes('T')
      ? value.replace(' ', 'T') + 'Z'
      : value;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const selectedNames = useMemo(
    () => new Set(seedArtists.map((a) => a.name)),
    [seedArtists]
  );

  const handleAddSeed = (artist) => {
    dispatch({
      type: ADD_SEED_ARTIST,
      payload: {
        id: artist.artistId || `disc-${artist.artistName}`,
        name: artist.artistName,
        genres: [],
        image: artist.artistImage,
      },
    });
  };

  const handlePlayArtist = useCallback(async (artist) => {
    const artistName = artist.artistName;

    // If already playing this artist, toggle
    if (audio.currentTrack?.artistName === artistName) {
      audio.toggle();
      return;
    }

    // Check cache
    if (trackCache.current[artistName]) {
      audio.play(trackCache.current[artistName]);
      return;
    }

    // Fetch top track
    setLoadingTrack(artistName);
    try {
      const track = await findArtistTrack(artistName, artist.artistId);
      if (track) {
        trackCache.current[artistName] = { ...track, artistName };
        audio.play(trackCache.current[artistName]);
      }
    } finally {
      setLoadingTrack(null);
    }
  }, [audio]);

  const handleShareArtist = useCallback(async (artist) => {
    const artistUrl = buildArtistPageUrl(artist.artistName);
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${artist.artistName} \u2014 Stellar`,
          url: artistUrl,
        });
      } catch {
        // User cancelled or share failed
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(artistUrl);
    } catch {
      // Clipboard unavailable
    }
  }, []);

  return (
    <div className="discovered-section">
      {/* Signed out — placeholder */}
      {!user && (
        <div className="favorites-scroll">
          <button
            className="section-placeholder-card"
            onClick={() => showAuthModal('register')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="section-placeholder-text">Sign in to save discovered artists</span>
          </button>
        </div>
      )}

      {/* Signed in, no discoveries — empty placeholder */}
      {user && discoveredArtists.length === 0 && (
        <div className="favorites-scroll">
          <div className="section-placeholder-card empty">
            <span className="section-placeholder-text">Saved discoveries will appear here</span>
          </div>
        </div>
      )}

      {/* Signed in, has discoveries — horizontal scroll */}
      {user && discoveredArtists.length > 0 && (
        <div className="favorites-scroll">
          {filteredDiscovered.map((artist) => (
            <div key={artist.artistName} className={`discovered-card ${selectedNames.has(artist.artistName) ? 'selected' : ''}`}>
              <button
                className="discovered-card-remove"
                onClick={() => setConfirmRemove(artist)}
                title="Remove from discoveries"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
              <button
                className="discovered-card-main"
                onClick={() => handleAddSeed(artist)}
                title={`Add ${artist.artistName} as seed`}
              >
                {artist.artistImage ? (
                  <img className="discovered-card-image" src={artist.artistImage} alt={artist.artistName} />
                ) : (
                  <div className="discovered-card-image discovered-card-placeholder">
                    {artist.artistName.charAt(0)}
                  </div>
                )}
                <span className="discovered-card-text">
                  <span className="discovered-card-name">{artist.artistName}</span>
                  {artist.addedAt && (
                    <span className="discovered-card-date">Discovered {formatDiscoveredDate(artist.addedAt)}</span>
                  )}
                  {discoveredConnections[artist.artistName]?.length > 0 && (
                    <span className="discovered-card-connections" title={`Connected to ${discoveredConnections[artist.artistName].map((c) => c.name).join(', ')}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10" aria-hidden="true">
                        <circle cx="6" cy="6" r="2.5" />
                        <circle cx="18" cy="18" r="2.5" />
                        <line x1="8" y1="8" x2="16" y2="16" />
                      </svg>
                      <span className="discovered-card-connections-text">
                        {discoveredConnections[artist.artistName].map((c, i) => (
                          <span key={c.name} className={`discovered-card-connection-name${c.source === 'favorite' ? ' is-favorite' : ''}`}>
                            {i > 0 && <span className="discovered-card-connection-sep">·</span>}
                            {c.name}
                          </span>
                        ))}
                      </span>
                    </span>
                  )}
                </span>
              </button>
              <div className="discovered-card-actions">
                <button
                  className={`discovered-card-action-btn discovered-card-play-btn${audio.isPlaying && audio.currentTrack?.artistName === artist.artistName ? ' playing' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handlePlayArtist(artist); }}
                  title={audio.isPlaying && audio.currentTrack?.artistName === artist.artistName ? 'Pause' : `Play ${artist.artistName}`}
                  disabled={loadingTrack === artist.artistName}
                >
                  {audio.currentTrack?.artistName === artist.artistName && audio.progress > 0 && (
                    <svg className="preview-progress-ring discovered-card-progress-ring" viewBox="0 0 34 34">
                      <circle
                        cx="17" cy="17" r="15"
                        fill="none"
                        stroke="rgba(255, 215, 0, 0.2)"
                        strokeWidth="2"
                      />
                      <circle
                        cx="17" cy="17" r="15"
                        fill="none"
                        stroke="rgba(255, 215, 0, 0.8)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 15}
                        strokeDashoffset={2 * Math.PI * 15 * (1 - (audio.progress || 0))}
                      />
                    </svg>
                  )}
                  {loadingTrack === artist.artistName ? (
                    <svg className="share-spinner" viewBox="0 0 24 24" width="12" height="12">
                      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="31.4 31.4" />
                    </svg>
                  ) : audio.isPlaying && audio.currentTrack?.artistName === artist.artistName ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                      <polygon points="5,3 19,12 5,21" />
                    </svg>
                  )}
                </button>
                <button
                  className="discovered-card-action-btn discovered-card-share-btn"
                  onClick={(e) => { e.stopPropagation(); handleShareArtist(artist); }}
                  title={`Share ${artist.artistName}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {confirmRemove && (
        <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmRemove(null)}>
          <div className="confirm-remove-modal">
            <p className="confirm-remove-text">
              Remove <strong>{confirmRemove.artistName}</strong> from discoveries?
            </p>
            <div className="confirm-remove-actions">
              <button className="confirm-remove-cancel" onClick={() => setConfirmRemove(null)}>
                Cancel
              </button>
              <button className="confirm-remove-confirm" onClick={() => {
                toggleDiscoveredArtist(confirmRemove.artistName, confirmRemove.artistId, confirmRemove.artistImage);
                setConfirmRemove(null);
              }}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DiscoveredSection;
